import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    ReactNode,
    HTMLAttributes
} from "react"

interface AnimateHeightProps extends HTMLAttributes<HTMLDivElement> {
    minified?: boolean
    maxHeightInPx?: number
    children: ReactNode
}

const AnimateHeight: React.FC<AnimateHeightProps> = ({
    minified = true,
    children,
    maxHeightInPx,
    ...rest
}) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const [maxHeight, setMaxHeight] = useState("0px")
    const [isTransitioning, setIsTransitioning] = useState(false)

    // Set maxHeight initially and on minified changes
    const updateHeight = useCallback(() => {
        if (ref.current) {
            const fullHeight = ref.current.scrollHeight
            const height = minified
                ? "0px"
                : `${Math.min(fullHeight, maxHeightInPx || fullHeight)}px`
            setMaxHeight(height)
            setIsTransitioning(true)
        }
    }, [minified, maxHeightInPx])

    // Adjust height after mount
    useEffect(() => {
        updateHeight()
    }, [updateHeight])

    // Apply transition after initial load
    useEffect(() => {
        if (!isTransitioning) {
            const timer = setTimeout(() => setIsTransitioning(true), 0)
            return () => clearTimeout(timer)
        }
    }, [isTransitioning])

    return (
        <div
            ref={ref}
            style={{
                maxHeight,
                overflow: "hidden",
                transition: isTransitioning ? "max-height 0.3s ease" : "none"
            }}
            {...rest}
        >
            {children}
        </div>
    )
}

export default AnimateHeight
